import {
  DefaultSchoolYear,
  EvaluationType,
  FilterKeys,
  GetReportsQueryFilters,
  ReportStatuses,
} from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import {
  EvaluationTypeSelectMultiple,
  FiltersSidebar,
  FiltersSidebarMethods,
  ReportStatusSelectMultiple,
  SchoolPeriodsRangeSelect,
  SchoolPeriodsRangeSelectComponent,
  toggleMultipleValueArrayProperty,
  useFiltersSidebar,
  UserSelectMultiple,
} from '@schooly/components/filters';
import { forwardRef, useCallback, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { useReportsFilters } from './useReportsFilters';

type ReportsFiltersSidebarProps = {
  defaultSchoolYear?: DefaultSchoolYear;
  filters: GetReportsQueryFilters;
  defaultFilters: GetReportsQueryFilters;
  schoolId: string;
  onApply: (v: GetReportsQueryFilters) => void;
};

export const ReportsFiltersSidebar = forwardRef<FiltersSidebarMethods, ReportsFiltersSidebarProps>(
  ({ filters: propsFilters, defaultFilters, onApply, schoolId, defaultSchoolYear }, ref) => {
    const [filters, setFilters] = useState(propsFilters);
    const { currentStaff, permissions } = useAuth();
    const { formatMessage } = useIntl();
    const periodsRef = useRef<SchoolPeriodsRangeSelectComponent>(null);
    const { allowClose, preventClose, sidebar } = useFiltersSidebar(ref);
    const {
      labels,
      onSetDate,
      onSetReportStatus,
      onClearReportStatus,
      onSetStaff,
      onClearStaff,
      onSetEvaluationType,
      onClearEvaluationType,
      onSetAreaOfLearning,
    } = useReportsFilters({ filters, onSetFilters: setFilters });

    const handleApply = useCallback(() => {
      onApply(filters);
    }, [filters, onApply]);

    const handleClear = useCallback(() => {
      setFilters(defaultFilters);
      if (defaultFilters.date) {
        periodsRef.current?.updateDates(defaultFilters.date);
      }
    }, [defaultFilters]);

    const onSelectReportStatus = useCallback(
      (status: ReportStatuses) => {
        onSetReportStatus(toggleMultipleValueArrayProperty(filters.report_status, [status]));
      },
      [filters.report_status, onSetReportStatus],
    );

    const onSelectStaff = useCallback(
      (staffId: string) => {
        onSetStaff(toggleMultipleValueArrayProperty(filters.staff, [staffId]));
      },
      [filters.staff, onSetStaff],
    );

    const onSelectEvaluationType = useCallback(
      (evaluationType: EvaluationType) => {
        onSetEvaluationType(
          toggleMultipleValueArrayProperty(filters.evaluation_type, [evaluationType]),
        );
      },
      [filters.evaluation_type, onSetEvaluationType],
    );

    const onSelectAreaOfLearning = useCallback(
      (areaOfLearningId: string) => {
        onSetAreaOfLearning(
          toggleMultipleValueArrayProperty(filters.area_of_learning, [areaOfLearningId]),
        );
      },
      [filters.area_of_learning, onSetAreaOfLearning],
    );

    const dateFilter = useMemo(() => ({ [FilterKeys.Date]: filters.date }), [filters.date]);

    return (
      <FiltersSidebar
        onOpen={() => {
          setFilters(propsFilters);
        }}
        ref={sidebar}
        title={formatMessage(
          { id: 'filter-FiltersForPage' },
          { page: formatMessage({ id: 'section-Reports' }) },
        )}
        onClear={handleClear}
        onApply={handleApply}
      >
        <SchoolPeriodsRangeSelect
          ref={periodsRef}
          showSelectedPeriodLabel={false}
          schoolId={schoolId}
          defaultSchoolYear={defaultSchoolYear}
          hasManagePermission={permissions.includes('school_admin')}
          onSetDate={onSetDate}
          placeholder={labels[FilterKeys.Date]}
          date={filters.date || []}
          requiredLabel="required"
          onOpen={preventClose}
          onClose={allowClose}
        />

        <ReportStatusSelectMultiple
          placeholder={labels[FilterKeys.ReportStatus]}
          onSelectStatus={onSelectReportStatus}
          selectedStatuses={filters.report_status || []}
          onClear={onClearReportStatus}
          onOpen={preventClose}
          onClose={allowClose}
        />

        <UserSelectMultiple
          type="staff"
          currentUser={currentStaff}
          placeholder={labels[FilterKeys.Staff]}
          schoolId={schoolId}
          onSelectUserId={onSelectStaff}
          selectedIds={filters.staff || []}
          onClear={onClearStaff}
          filters={dateFilter}
          onOpen={preventClose}
          onClose={allowClose}
        />

        <EvaluationTypeSelectMultiple
          placeholder={labels[FilterKeys.EvaluationType]}
          schoolId={schoolId}
          selectedEvaluationTypes={filters.evaluation_type || []}
          selectedAreaOfLearningIds={filters.area_of_learning || []}
          onSelectEvaluationType={onSelectEvaluationType}
          onSelectAreaOfLearning={onSelectAreaOfLearning}
          onClear={onClearEvaluationType}
          onOpen={preventClose}
          onClose={allowClose}
        />
      </FiltersSidebar>
    );
  },
);
